import { useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { type DeepPartial } from "react-hook-form";

import { useGetAggregatedEntityIds } from "@ds/hooks/use-get-aggregated-entity-ids";
import { useParseQueryIds } from "@ds/hooks/use-parse-query-ids";
import { useReduxEntitiesFetch } from "@ds/hooks/use-redux-entities-fetch";
import { useVisibility } from "@ds/hooks/use-visibility";
import { QueryParamType, useDeepRootSelector, useRootDispatch, useRootSelector } from "@ds/hooks";

import { EntityTypeName } from "@ds/model/entity-model";

import { MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { DetailsView } from "@ds/components/details-view";
import { AbsoluteLoader } from "@ds/components/loaders/loading";
import { CAMPAIGN_ID_PARAM, CONTENT_CAMPAIGNS_ROUTE } from "@ds/constants/router";
import { pluralizeTotalSelectedTitle } from "@ds/utils/localization/common";
import { isMultiple } from "@ds/utils/numbers";

import { CampaignDetailsInfoCard } from "./components/details/campaign-details-info-card";
import { CampaignDetailsItemsCard } from "./components/details/campaign-details-items-card";
import { DuplicateCampaignModal } from "./components/forms/duplicate-campaign-form/duplicate-campaign-modal";
import { selectAggregatedCampaignByIds, selectCampaignsIsLoading } from "./redux/selectors";
import { campaignsActions } from "./redux/slice";

export const CampaignDetailsPage: FC = () => {
  const navigate = useNavigate();
  const queryIds = useParseQueryIds(CAMPAIGN_ID_PARAM, QueryParamType.Number);

  const campaign = useDeepRootSelector(state => selectAggregatedCampaignByIds(state, queryIds));
  const aggregatedCampaignIds = useGetAggregatedEntityIds(campaign);

  const isLoading = useRootSelector(selectCampaignsIsLoading);

  const [toggleRefetch] = useReduxEntitiesFetch(
    { entityType: EntityTypeName.CONTENT_CAMPAIGN, filters: { id: queryIds } },
    { initialLoad: true },
  );

  const [isDuplicateModalVisible, setIsDuplicateModalVisible] = useVisibility();

  const dispatch = useRootDispatch();
  const onSubmitHandler = useCallback(
    (changedFields: DeepPartial<Campaign>) =>
      dispatch(campaignsActions.updateCampaigns(aggregatedCampaignIds, changedFields)),
    [dispatch, aggregatedCampaignIds],
  );

  if (!campaign) {
    return <AbsoluteLoader isLoading />;
  }

  return (
    <>
      <DetailsView
        title={
          isMultiple(campaign?.metadata?.totalAggregated)
            ? pluralizeTotalSelectedTitle("campaign", campaign?.metadata?.totalAggregated)
            : campaign.name
        }
        tableType={MainTableDataTypeEnum.ContentCampaigns}
        isLoading={isLoading}
        onRefresh={toggleRefetch}
      >
        <CampaignDetailsInfoCard
          campaign={campaign}
          onSubmit={onSubmitHandler}
          onDuplicate={setIsDuplicateModalVisible(true)}
        />
        <CampaignDetailsItemsCard campaign={campaign} />
      </DetailsView>

      {isDuplicateModalVisible && (
        <DuplicateCampaignModal
          campaignId={queryIds[0]}
          onClose={setIsDuplicateModalVisible(false)}
          onSuccess={() => navigate(CONTENT_CAMPAIGNS_ROUTE)}
        />
      )}
    </>
  );
};
